import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import * as referralApi from '../../api/referral.api';
import type { ReferralInfo, ReferralStats } from '../../types/referral';
import styles from './ReferralPage.module.css';

export function ReferralPage() {
  const [link, setLink] = useState<ReferralInfo | null>(null);
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedTg, setCopiedTg] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const [linkData, statsData] = await Promise.allSettled([
          referralApi.getReferralLink(),
          referralApi.getReferralStats(),
        ]);
        if (linkData.status === 'fulfilled') setLink(linkData.value);
        if (statsData.status === 'fulfilled') setStats(statsData.value);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available
    }
  };

  const handleCopyTg = async () => {
    if (!link?.tgUrl) return;
    try {
      await navigator.clipboard.writeText(link.tgUrl);
      setCopiedTg(true);
      setTimeout(() => setCopiedTg(false), 2000);
    } catch {
      // Clipboard API may not be available
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Реферальная программа</h1>
      <p className={styles.subtitle}>
        Пригласите друга — вы оба получите бонусные дни к подписке.
        Каждому начисляется бонус согласно его собственному тарифу.
      </p>

      {link && (
        <Card className={styles.card}>
          <div className={styles.linkSection}>
            <p className={styles.label}>Ваша реферальная ссылка (сайт)</p>
            <div className={styles.linkRow}>
              <input
                className={styles.linkInput}
                value={link.url}
                readOnly
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <Button variant="primary" size="sm" onClick={handleCopy}>
                {copied ? 'Скопировано' : 'Копировать'}
              </Button>
            </div>
            {copied && <p className={styles.copied}>Ссылка скопирована!</p>}

            {link.tgUrl && (
              <>
                <p className={styles.label} style={{ marginTop: 16 }}>
                  Ссылка для Telegram-бота
                </p>
                <div className={styles.linkRow}>
                  <input
                    className={styles.linkInput}
                    value={link.tgUrl}
                    readOnly
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <Button variant="primary" size="sm" onClick={handleCopyTg}>
                    {copiedTg ? 'Скопировано' : 'Копировать'}
                  </Button>
                </div>
                {copiedTg && <p className={styles.copied}>Ссылка скопирована!</p>}
              </>
            )}
          </div>

          <div className={styles.qrSection}>
            <QRCodeSVG value={link.url} size={160} />
          </div>
        </Card>
      )}

      <Card className={styles.tiersCard}>
        <div className={styles.tiersHeader}>Бонус по вашему тарифу</div>
        <div className={styles.tiersGrid}>
          <div className={styles.tier}>
            <div className={styles.tierPlan}>1 месяц</div>
            <div className={styles.tierArrow}>→</div>
            <div className={styles.tierBonus}>+3 дня</div>
          </div>
          <div className={styles.tier}>
            <div className={styles.tierPlan}>3 месяца</div>
            <div className={styles.tierArrow}>→</div>
            <div className={styles.tierBonus}>+5 дней</div>
          </div>
          <div className={styles.tier}>
            <div className={styles.tierPlan}>6 месяцев</div>
            <div className={styles.tierArrow}>→</div>
            <div className={styles.tierBonus}>+7 дней</div>
          </div>
        </div>
        <p className={styles.tiersNote}>
          <strong>Вы</strong> получаете бонус по вашему тарифу, <strong>друг</strong> — по своему.
          <br />
          Пример: у вас 6 месяцев, друг оплачивает 1 месяц — вам +7 дней, ему +3 дня.
        </p>
      </Card>

      <p className={styles.warning}>
        Бонусные дни начисляются только при наличии активной платной подписки у обоих пользователей.
      </p>

      {stats && (
        <>
          <div className={styles.statsGrid}>
            <Card className={styles.statCard}>
              <div className={styles.statValue}>{stats.totalReferred}</div>
              <div className={styles.statLabel}>Приглашено человек</div>
            </Card>
            <Card className={styles.statCard}>
              <div className={styles.statValue}>{stats.totalBonusDays}</div>
              <div className={styles.statLabel}>Бонусных дней</div>
            </Card>
          </div>

          {stats.referrals.length > 0 && (
            <div className={styles.referralsList}>
              <h2 className={styles.referralsTitle}>Приглашённые</h2>
              <Card>
                {stats.referrals.map((ref) => (
                  <div key={ref.id} className={styles.referralItem}>
                    <span className={styles.referralEmail}>{ref.refereeDisplay}</span>
                    <div className={styles.referralMeta}>
                      <Badge variant="success">+{ref.bonusDays} дн.</Badge>
                      <span>{formatDate(ref.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
